/**
 * AdvancedPolicy — the full policy graph editor: axes, evidence, and the
 * simulate/canary/rollback lifecycle. Both the Autopilot preset and the
 * Advanced editor compile through the SAME public policy engine: the
 * preset flow produces a PolicyForm and serializes to the identical
 * canonical bytes the advanced editor submits.
 */

import type { ReactElement } from "react";
import type { PolicyForm } from "../policy/roundtrip.js";

export interface AdvancedPolicyProps {
  readonly form: PolicyForm;
  readonly activeVersion: number | null;
  readonly canaryVersion: number | null;
  readonly onSimulate: () => void;
  readonly onCanary: (version: number) => void;
  readonly onActivate: (version: number) => void;
  readonly onRollback: (version: number) => void;
  readonly simulationResult?: string;
}

export function AdvancedPolicy(props: AdvancedPolicyProps): ReactElement {
  return (
    <section className="advanced-policy" aria-label="Advanced policy editor">
      <h3>Policy: {props.form.name}</h3>
      <div className="policy-axes">
        <h4>Axes ({props.form.axes.length})</h4>
        <ul>
          {props.form.axes.map((axis, i) => (
            <li key={`${axis.kind}/${axis.subject}/${i}`}>
              {axis.kind}/{axis.subject} = {axis.value}
            </li>
          ))}
        </ul>
      </div>
      <div className="policy-evidence">
        <h4>Evidence ({props.form.evidence.length})</h4>
        <ul>
          {props.form.evidence.map((ev, i) => (
            <li key={`${ev.subject}/${i}`}>
              {ev.subject}: <code>{ev.digest}</code>
            </li>
          ))}
        </ul>
      </div>
      <div className="policy-lifecycle">
        <button type="button" onClick={props.onSimulate}>
          Simulate
        </button>
        <button type="button" onClick={() => props.onActivate(props.form.axes.length)}>
          Activate
        </button>
        <button type="button" onClick={() => props.onRollback(props.form.axes.length)}>
          Rollback
        </button>
        {props.activeVersion !== null ? <span>active v{props.activeVersion}</span> : null}
        {props.canaryVersion !== null ? <span>canary v{props.canaryVersion}</span> : null}
      </div>
      {props.simulationResult !== undefined ? <pre className="policy-simulation">{props.simulationResult}</pre> : null}
    </section>
  );
}
